import { useMemo } from 'react';
import type { User } from '@/api/auth';
import type { GameV1 } from '@/api/games';
import { snakify } from '@/helpers/common';
import { usePlayerFields } from '@/modules/common/hooks/usePlayerFields';
import { useGetCommonStore } from '@/store';
import { isPlayerFieldsFilled } from './profile';

export const usePlayerFieldsStatus = (
  provider?: GameV1['provider']
): {
  requiredFields: (keyof User)[];
  isAllFieldsFilled: boolean;
  missingFields: string[];
} => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();

  const { data: playerFields } = usePlayerFields();

  return useMemo(() => {
    if (!(user && playerFields && provider)) {
      return {
        requiredFields: [],
        isAllFieldsFilled: false,
        missingFields: [],
      };
    }

    const snakeUser = snakify(user) as User;
    const raw = Array.isArray(playerFields.contexts?.[provider])
      ? playerFields.contexts?.[provider]
      : [];

    const required = raw.filter((k): k is keyof User => k in snakeUser);

    const { isAllFieldsFilled, missingFields } = isPlayerFieldsFilled(
      snakeUser,
      required
    );

    return { requiredFields: required, isAllFieldsFilled, missingFields };
  }, [user, playerFields, provider]);
};
